<template>
  <div class="customer-history-container">
    <!-- Header Section -->
    <VCard class="mb-6 customer-history-header">
      <VCardTitle class="d-flex align-center justify-space-between">
        <div class="d-flex align-center customer-history-title">
          <VIcon
            icon="tabler-users-history"
            class="me-3"
            size="28"
            color="primary"
          />
          <div>
            <h2 class="text-h5 mb-0">{{ $t('history.customers.title') }}</h2>
            <p class="text-subtitle-2 mb-0 text-medium-emphasis">
              {{ $t('history.customers.subtitle') }}
            </p>
          </div>
        </div>
        
        <div class="d-flex align-center gap-3">
          <!-- View Toggle -->
          <VBtnToggle
            v-model="viewMode"
            mandatory
            variant="outlined"
            divided
            density="compact"
            class="view-toggle"
          >
            <VBtn value="table" icon="tabler-table" size="small" />
            <VBtn value="card" icon="tabler-layout-grid" size="small" />
          </VBtnToggle>
          
          <VBtn
            color="primary"
            variant="outlined"
            prepend-icon="tabler-download"
            class="customer-history-export-btn"
            @click="exportData"
          >
            {{ $t('history.customers.export_customer_data') }}
          </VBtn>
        </div>
      </VCardTitle>
        
      <!-- Filters Section -->
      <VCardText>
        <VRow class="customer-history-filters">
          <VCol cols="12" md="4">
            <VTextField
              v-model="searchQuery"
              :label="$t('history.customers.search_customer')"
              prepend-inner-icon="tabler-search"
              density="compact"
              hide-details
              clearable
              variant="outlined"
              class="customer-search-field"
              @keyup.enter="fetchCustomers"
            />
          </VCol>
            
          <VCol cols="12" md="3">
            <VSelect
              v-model="selectedPeriod"
              :items="periodOptions"
              :label="$t('history.customers.filter_period')"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="period-filter"
              @update:model-value="fetchCustomers"
            />
          </VCol>
            
          <VCol cols="12" md="3">
            <VSelect
              v-model="selectedStatus"
              :items="statusOptions"
              :label="$t('history.customers.filter_status')"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="status-filter"
              @update:model-value="fetchCustomers"
            />
          </VCol>
            
          <VCol cols="12" md="2" class="d-flex align-center">
            <VBtn 
              color="primary" 
              class="filter-apply-btn"
              :loading="isLoading"
              @click="fetchCustomers"
            >
              {{ $t('history.filters.apply_filters') }}
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  
    <!-- Customer History Content -->
    <VCard class="customer-history-content">
      <VCardText>
        <div v-if="isLoading" class="text-center py-8">
          <VProgressCircular indeterminate color="primary" />
        </div>
          
        <div v-else-if="customers.length === 0" class="text-center py-8 no-data-section">
          <VIcon icon="tabler-users-off" size="64" class="mb-4 text-disabled" />
          <h3 class="text-h6 mb-2">{{ $t('history.customers.no_history') }}</h3>
        </div>
          
        <!-- Table View -->
        <div v-else-if="viewMode === 'table'">
          <VDataTable
            :items="customers"
            :headers="tableHeaders"
            :loading="isLoading"
            class="customer-history-table"
            :items-per-page="itemsPerPage"
            :page="currentPage"
            hide-default-footer
          >
            <template #item.name="{ item }">
              <div class="d-flex align-center">
                <VAvatar size="32" color="primary" variant="tonal" class="me-3">
                  <VIcon icon="tabler-user" size="16" />
                </VAvatar>
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption text-disabled">{{ item.phone }}</div>
                </div>
              </div>
            </template>
            
            <template #item.completed_orders="{ item }">
              <VChip color="primary" size="small">
                {{ item.completed_orders || 0 }}
              </VChip>
            </template>
            
            <template #item.total_weight="{ item }">
              <div class="d-flex align-center">
                <VIcon icon="tabler-weight" size="16" class="me-1" />
                <span class="font-weight-bold">{{ formatWeight(item.total_weight || 0) }}</span>
              </div>
            </template>
            
            <template #item.total_volume="{ item }">
              <div class="d-flex align-center">
                <VIcon icon="tabler-box" size="16" class="me-1" />
                <span class="font-weight-bold">{{ formatVolume(item.total_volume || 0) }}</span>
              </div>
            </template>
            
            <template #item.satisfaction="{ item }">
              <VRating
                :model-value="item.satisfaction || 5"
                size="small"
                color="warning"
                readonly
                density="compact"
              />
            </template>
            
            <template #item.last_order_date="{ item }">
              {{ formatDate(item.last_order_date) }}
            </template>
            
            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  color="primary"
                  :to="`/history/customers/${item.id}`"
                >
                  <VIcon icon="tabler-eye" />
                </VBtn>
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  color="secondary"
                  @click="generateCustomerReport(item)"
                >
                  <VIcon icon="tabler-file-analytics" />
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </div>
        
        <!-- Card View -->
        <div v-else>
          <VRow>
            <VCol 
              v-for="customer in customers" 
              :key="customer.id" 
              cols="12" 
              md="6" 
              xl="4"
            >
              <VCard 
                variant="outlined" 
                class="customer-card"
                hover
                :to="`/history/customers/${customer.id}`"
              >
                <VCardText class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <VAvatar size="40" color="primary" variant="tonal" class="me-3">
                      <VIcon icon="tabler-user" />
                    </VAvatar>
                    <div class="flex-grow-1">
                      <h5 class="text-subtitle-1 mb-1">{{ customer.name }}</h5>
                      <p class="text-caption text-disabled mb-0">
                        {{ $t('history.customers.customer_since') }}: {{ formatDate(customer.created_at) }}
                      </p>
                    </div>
                  </div>
                    
                  <VRow dense class="customer-stats">
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-h6 text-primary">{{ customer.completed_orders || 0 }}</div>
                        <div class="text-caption">{{ $t('history.customers.completed_orders') }}</div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <VRating
                          :model-value="customer.satisfaction || 5"
                          size="small"
                          color="warning"
                          readonly
                          density="compact"
                          class="mb-1"
                        />
                        <div class="text-caption">{{ $t('history.customers.satisfaction') }}</div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-subtitle-2 text-success">{{ formatWeight(customer.total_weight || 0) }}</div>
                        <div class="text-caption">{{ $t('history.customers.total_weight') }}</div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-subtitle-2 text-info">{{ formatVolume(customer.total_volume || 0) }}</div>
                        <div class="text-caption">{{ $t('history.customers.total_volume') }}</div>
                      </div>
                    </VCol>
                  </VRow>
                </VCardText>
                  
                <VCardActions class="px-4 pb-3">
                  <VBtn
                    color="primary"
                    variant="text"
                    size="small"
                    prepend-icon="tabler-eye"
                    @click.prevent="$router.push(`/history/customers/${customer.id}`)"
                  >
                    {{ $t('history.customers.view_details') }}
                  </VBtn>
                  <VSpacer />
                  <VBtn
                    color="secondary"
                    variant="text" 
                    size="small"
                    icon="tabler-file-analytics"
                    @click.prevent="generateCustomerReport(customer)"
                  />
                </VCardActions>
              </VCard>
            </VCol>
          </VRow>
        </div>
        
        <!-- Pagination -->
        <div v-if="totalPages > 1" class="d-flex justify-center mt-6">
          <VPagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="7"
            class="customer-pagination"
            @update:model-value="fetchCustomers"
          />
        </div>
      </VCardText>
    </VCard>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'
  
definePage({
  meta: {
    requiresAuth: true,
    role: ['admin', 'manager', 'user'],
  },
})
  
const { t } = useI18n()
const { showSnackbar } = useSnackbar()
  
// Reactive data
const customers = ref([])
const isLoading = ref(false)
const searchQuery = ref('')
const selectedPeriod = ref('')
const selectedStatus = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const totalItems = ref(0)
const viewMode = ref('table') // Default to table view
  
// Computed
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

// Table headers
const tableHeaders = computed(() => [
  { title: t('shipment.customer_name'), key: 'name', sortable: true },
  { title: t('history.customers.completed_orders'), key: 'completed_orders', sortable: true },
  { title: t('history.customers.total_weight'), key: 'total_weight', sortable: true },
  { title: t('history.customers.total_volume'), key: 'total_volume', sortable: true },
  { title: t('history.customers.satisfaction'), key: 'satisfaction', sortable: true },
  { title: t('history.customers.last_order'), key: 'last_order_date', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false, width: '100' },
])
  
// Filter options
const periodOptions = computed(() => [
  { title: t('history.filters.all_time'), value: '' },
  { title: t('history.filters.last_30_days'), value: '30' },
  { title: t('history.filters.last_90_days'), value: '90' },
  { title: t('history.filters.last_6_months'), value: '180' },
  { title: t('history.filters.last_year'), value: '365' },
])
  
const statusOptions = computed(() => [
  { title: t('shipment.all'), value: '' },
  { title: t('shipment.statuses.completed'), value: 'completed' },
  { title: t('shipment.statuses.delivered'), value: 'delivered' },
])
  
// Methods
const fetchCustomers = async () => {
  try {
    isLoading.value = true
      
    const params = {
      page: currentPage.value,
      per_page: itemsPerPage.value,
      search: searchQuery.value,
      period: selectedPeriod.value,
      status: selectedStatus.value,
      with_completed_orders: true,
    }
      
    const response = await axios.get('/shipment/history/customers', { params })
      
    customers.value = response.data.data
    totalItems.value = response.data.total
  } catch (error) {
    console.error('Error fetching customer history:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'customer history' }),
    })
  } finally {
    isLoading.value = false
  }
}
  
const formatDate = date => {
  if (!date) return '-'
  
  return new Date(date).toLocaleDateString()
}
  
const formatWeight = weight => {
  return `${weight.toFixed(1)} kg`
}

const formatVolume = volume => {
  return `${volume.toFixed(2)} m³`
}
  
const generateCustomerReport = async customer => {
  try {
    const response = await axios.get(`/shipment/history/customers/${customer.id}/report`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', `customer-report-${customer.id}.pdf`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.report_generated'),
    })
  } catch (error) {
    console.error('Error generating customer report:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.report_generation_failed'),
    })
  }
}
  
const exportData = async () => {
  try {
    const response = await axios.get('/shipment/history/customers/export', {
      params: {
        search: searchQuery.value,
        period: selectedPeriod.value,
        status: selectedStatus.value,
      },
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', 'customer-history.xlsx')
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.export_successful'),
    })
  } catch (error) {
    console.error('Error exporting data:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.export_failed'),
    })
  }
}
  
// Lifecycle
onMounted(() => {
  fetchCustomers()
})
</script>
  
<style scoped>
.customer-history-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.customer-history-header {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.customer-history-title {
  flex-grow: 1;
}

.view-toggle {
  border-radius: 6px;
}

.customer-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  height: fit-content;
}

.customer-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.customer-stats {
  border-top: 1px solid rgb(var(--v-theme-surface-variant));
  margin-top: 12px;
  padding-top: 12px;
}

.no-data-section {
  opacity: 0.7;
}

.customer-history-filters {
  border-radius: 8px;
  padding: 16px;
  margin: -8px;
}

.customer-history-table {
  border-radius: 8px;
}

.customer-history-content {
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

@media (max-width: 768px) {
  .view-toggle {
    order: 1;
    width: 100%;
    margin-bottom: 8px;
  }
  
  .customer-history-export-btn {
    order: 2;
    width: 100%;
  }
}
</style>